import { CalendarClock, CheckCircle2 } from "lucide-react";
import { daysUntil, formatDate, formatINR, freqLabel, typeMeta } from "./policyShared";

/**
 * Right-rail premium schedule for the next 12 months. Extracted from PoliciesPage.jsx
 * during 08-Feb refactor. Presentation-only — parent owns fetching.
 *
 * Props:
 *   - items: array of PolicyCalendarItem from GET /policies/premium-calendar?months=12
 *   - loading: boolean spinner control
 */
export function PremiumCalendarSidebar({ items, loading }) {
  return (
    <aside className="sticky top-20 h-fit">
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="premium-calendar">
        <div className="mb-3 flex items-center gap-2 border-b border-border pb-2">
          <CalendarClock className="h-4 w-4 text-gold" />
          <h3 className="text-sm font-semibold uppercase tracking-widest text-text">
            Premium calendar
          </h3>
        </div>
        <p className="mb-3 text-xs text-muted">
          Next 12 months, across all policies. One-time premiums are excluded.
        </p>
        {loading ? (
          <p className="text-xs text-muted">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-muted">
            No recurring premiums scheduled yet. Add a policy with a next-renewal date to see it
            here.
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {items.slice(0, 12).map((item, i) => {
              const days = daysUntil(item.due_date);
              const soon = days != null && days >= 0 && days <= 30;
              const meta = typeMeta(item.policy_type);
              return (
                <li
                  key={`${item.policy_id}-${item.due_date}-${i}`}
                  className={`flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 ${item.paid ? "opacity-60" : ""}`}
                >
                  <div className="flex min-w-0 items-center gap-2">
                    {item.paid && <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-400" aria-label="Paid" />}
                    <div className="min-w-0">
                      <div className={`truncate text-sm ${item.paid ? "text-muted line-through" : "text-text"}`}>{item.policy_name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted">
                        {meta.label} · {freqLabel(item.frequency)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs font-medium ${item.paid ? "text-emerald-400" : "text-text"}`}>{formatDate(item.due_date)}</div>
                    <div className="text-[10px] text-muted">
                      {item.amount != null ? formatINR(item.amount) : ""}
                    </div>
                    {soon && !item.paid && (
                      <div className="mt-0.5 inline-block rounded-full bg-amber-400/15 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest text-amber-300">
                        {days}d
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
